import * as THREE from 'three'

/** A point picked by the measure tool, and whether it landed on a vertex. */
export interface MeasurePoint {
  point: THREE.Vector3
  snapped: boolean
}

/** How close, in screen pixels, the pointer must be to a vertex for it to snap. */
export const MEASURE_SNAP_RADIUS_PX = 12

const _v = new THREE.Vector3()
const _screen = new THREE.Vector3()

/**
 * Snap a measure-tool hit to the nearest vertex of the triangle under the pointer.
 *
 * Measuring a part's height or the gap between two parts means landing on a rim, a corner
 * or an edge, and a raw ray hit is a pixel or two off every time. The vertices of the hit
 * triangle are the candidates — the nearest vertex to a point on a surface is almost
 * always one of the corners of the triangle it lies in — so this stays constant-time on a
 * 400k-vertex model. The radius is in SCREEN pixels, so snapping feels the same at any
 * zoom: zoom in for a vertex a free click would have missed, out to grab it from afar.
 *
 * `enabled` false (Alt held) returns the raw hit.
 */
export function snapMeasurePoint(
  hit: { object: THREE.Object3D; face?: THREE.Face | null; point: THREE.Vector3 },
  camera: THREE.Camera,
  size: { width: number; height: number },
  enabled: boolean
): MeasurePoint {
  const raw: MeasurePoint = { point: hit.point.clone(), snapped: false }
  const mesh = hit.object as THREE.Mesh
  const position = (mesh.geometry as THREE.BufferGeometry | undefined)?.getAttribute('position')
  if (!enabled || !hit.face || !position) return raw

  _screen.copy(hit.point).project(camera)
  const px = ((_screen.x + 1) / 2) * size.width
  const py = ((1 - _screen.y) / 2) * size.height

  let best: THREE.Vector3 | null = null
  let bestPx = MEASURE_SNAP_RADIUS_PX
  for (const index of [hit.face.a, hit.face.b, hit.face.c]) {
    _v.fromBufferAttribute(position, index)
    mesh.localToWorld(_v)
    const world = _v.clone()
    _v.project(camera)
    const d = Math.hypot(((_v.x + 1) / 2) * size.width - px, ((1 - _v.y) / 2) * size.height - py)
    if (d <= bestPx) {
      bestPx = d
      best = world
    }
  }
  return best ? { point: best, snapped: true } : raw
}
